// Desk (brief section 8.1): decisions needed, upcoming commitments, active work and blockers.
// The personal desk covers every workspace of the person; a workspace desk covers one.
import { Link, useNavigate } from 'react-router';
import { DecisionRow, slugFor, TaskRow } from '../components/records';
import { CaptureForm, IdeaRow } from './Ideas';
import { Empty, ErrorBox, Loading, NotYet, PageHead } from '../components/ui';
import { useContextWorkspace } from '../components/Layout';
import { usd } from '../lib/format';
import { useSession } from '../lib/session';
import type { Desk as DeskData, Task } from '../lib/types';
import { useApi } from '../lib/useApi';

export function Desk() {
  const ws = useContextWorkspace();
  const { me } = useSession();
  const desk = useApi<DeskData>(ws ? `/api/w/${ws.slug}/desk` : '/api/desk');
  const d = desk.data;
  const navigate = useNavigate();
  const ideasPath = (slug: string | null) => (slug ? `/w/${slug}/ideas` : '/ideas');

  const replace = (t: Task) => {
    if (!d) return;
    const swap = (list: Task[]) => list.map((x) => (x.id === t.id ? { ...t, workspace: x.workspace } : x));
    desk.setData({ ...d, upcoming: swap(d.upcoming), active: swap(d.active), blocked: swap(d.blocked), proposed: swap(d.proposed) });
    void desk.reload();
  };
  const slugOf = (name: string | null | undefined) => (ws ? ws.slug : slugFor(me.workspaces, name));

  return (
    <>
      <PageHead
        title={ws ? `${ws.name} desk` : `Good to see you, ${me.user.name.split(' ')[0]}`}
        sub={
          d ? (
            <>
              Covers: {d.covers.join(', ') || 'no workspace'}
              {!ws && ' · plus your private records'}
            </>
          ) : undefined
        }
        actions={
          <Link className="btn primary" to={ws ? `/w/${ws.slug}/assistant` : '/assistant'}>
            Ask the {ws ? (ws.kind === 'joint' ? 'joint chief of staff' : 'chief of staff') : 'personal assistant'}
          </Link>
        }
      />
      {desk.error && <ErrorBox error={desk.error} retry={desk.reload} />}
      {!d && !desk.error && <Loading what="Loading your desk" />}
      {d && (
        <div className="desk-grid">
          <section className="panel span2">
            <h2>Decisions needed</h2>
            {d.decisions_needed.length ? (
              <ul className="recs">
                {d.decisions_needed.map((x) => (
                  <DecisionRow key={x.id} d={x} slug={slugOf(x.workspace)} showWorkspace={!ws} />
                ))}
              </ul>
            ) : (
              <Empty>No open decisions in {d.covers.join(', ')}.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>Capture an idea</h2>
            <CaptureForm onStored={() => void desk.reload()} />
          </section>
          <section className="panel">
            <h2>Ideas to revisit</h2>
            <p className="muted small">Stored ideas with a reason to come back now. None of them is a commitment.</p>
            {d.ideas_to_revisit.length ? (
              <ul className="recs">
                {d.ideas_to_revisit.map((i) => (
                  <IdeaRow key={i.id} idea={i} showWorkspace={!ws} onOpen={() => navigate(`${ideasPath(i.workspace_slug)}?open=${i.id}`)} />
                ))}
              </ul>
            ) : (
              <Empty>No stored idea is due to come back.</Empty>
            )}
            <Link to={ideasPath(ws ? ws.slug : null)} className="btn small">
              All {ws ? `${ws.name} ` : ''}ideas
            </Link>
          </section>
          <section className="panel">
            <h2>Upcoming commitments</h2>
            <p className="muted small">Unfinished tasks due in the next two weeks.</p>
            {d.upcoming.length ? (
              <ul className="recs">
                {d.upcoming.map((t) => (
                  <TaskRow key={t.id} task={t} slug={slugOf(t.workspace)} onChanged={replace} showWorkspace={!ws} />
                ))}
              </ul>
            ) : (
              <Empty>Nothing due in the next two weeks.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>Blocked</h2>
            <p className="muted small">Waiting for input or approval, or failed.</p>
            {d.blocked.length ? (
              <ul className="recs">
                {d.blocked.map((t) => (
                  <TaskRow key={t.id} task={t} slug={slugOf(t.workspace)} onChanged={replace} showWorkspace={!ws} />
                ))}
              </ul>
            ) : (
              <Empty>Nothing is blocked.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>Active work</h2>
            {d.active.length ? (
              <ul className="recs">
                {d.active.map((t) => (
                  <TaskRow key={t.id} task={t} slug={slugOf(t.workspace)} onChanged={replace} showWorkspace={!ws} />
                ))}
              </ul>
            ) : (
              <Empty>No task is ready or running.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>Proposed, not started</h2>
            {d.proposed.length ? (
              <ul className="recs">
                {d.proposed.map((t) => (
                  <TaskRow key={t.id} task={t} slug={slugOf(t.workspace)} onChanged={replace} showWorkspace={!ws} />
                ))}
              </ul>
            ) : (
              <Empty>No proposals waiting.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>Stale projects</h2>
            <p className="muted small">Exploring or active, untouched for {d.stale_after_days} days.</p>
            {d.stale_projects.length ? (
              <ul className="recs">
                {d.stale_projects.map((p) => (
                  <li key={p.id} className="rec">
                    <Link to={`/w/${slugOf(p.workspace)}/projects/${p.id}`}>{p.title}</Link>
                    {!ws && <span className="muted small"> · {p.workspace}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty>No project has gone quiet.</Empty>
            )}
          </section>
          <section className="panel">
            <h2>AI spend this month</h2>
            {ws && d.ai.enabled === false ? (
              <p className="msg warn">AI is switched off for this workspace: nothing is sent to Claude.</p>
            ) : ws && d.ai.paused ? (
              <p className="msg warn">AI work is paused (kill switch in Settings).</p>
            ) : null}
            <Meter used={d.ai.committed_this_month_usd} limit={d.ai.monthly_budget_usd} />
            <p className="muted small">
              {usd(d.ai.committed_this_month_usd)} of {usd(d.ai.monthly_budget_usd)} ({ws ? 'this workspace' : 'your personal assistant'}),
              measured cost plus reservations of runs in progress.
            </p>
          </section>
          {ws?.kind === 'joint' && (
            <section className="panel span2">
              <h2>Shared company context</h2>
              <NotYet title="Nothing has been shared into Joint Ventures yet.">
                Each company keeps its own information. From milestone 2, the joint chief of staff can <em>request company input</em>{' '}
                (for example “What capacity can Mirage commit next week?”); the company's owner reviews and approves the exact answer
                before it appears here.
              </NotYet>
              <button type="button" className="btn" disabled title="Arrives in milestone 2 (reviewed sharing)">
                Request company input
              </button>
            </section>
          )}
        </div>
      )}
    </>
  );
}

function Meter({ used, limit }: { used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 100;
  return (
    <div className="meter" role="meter" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used} aria-label="AI budget used">
      <span style={{ width: `${pct}%` }} className={pct > 85 ? 'hot' : ''} />
    </div>
  );
}
